import { createContext, useContext, useMemo, useState } from "react";

import {
  EltSyncLiteFragment,
  EltSyncSnapshotStateFragment,
  useEltSyncLiteQuery,
  useEltSyncSnapshotStateQuery,
} from "@/apollo/types";
import { useTimeout } from "@/hooks/useTimeout";
import { devLog } from "@/utils/logging";

import { useEltSyncUpdatedListener } from "./useEltSyncUpdatedListener";

export type EltSnapshotStateContextType = {
  sync?: EltSyncLiteFragment;
  snapshotState?: EltSyncSnapshotStateFragment;
  loading: boolean;
};

const EltSnapshotStateContext = createContext<EltSnapshotStateContextType>({
  loading: false,
});

export function EltSyncSnapshotStateProvider(props: {
  eltSyncId: string;
  autoRefetch?: boolean;
  /** Delay in ms before fetching data. Useful for virtualized lists to avoid
   *  firing requests for items that are only briefly scrolled into view. */
  delayMs?: number;
  children: React.ReactNode;
}) {
  const ctx = useEltSyncSnapshotState(props);
  return (
    <EltSnapshotStateContext.Provider value={ctx}>
      {props.children}
    </EltSnapshotStateContext.Provider>
  );
}

/**
 * Hook that returns `true` after the component has been mounted for at least
 * `delayMs` milliseconds. Returns `true` immediately when `delayMs` is 0 or
 * undefined. If the component unmounts before the delay elapses the timer is
 * cancelled and the queries are never executed.
 */
function useSettled(delayMs: number | undefined): boolean {
  const [settled, setSettled] = useState(!delayMs);
  useTimeout(() => setSettled(true), delayMs ?? null);
  return settled;
}

export function useEltSyncSnapshotState(props: {
  eltSyncId: string;
  autoRefetch?: boolean;
  /** Delay in ms before fetching data. Useful for virtualized lists. */
  delayMs?: number;
  /** Lets a caller use this hook unconditionally for a row that may not belong to a sync. */
  skip?: boolean;
}) {
  const settled = useSettled(props.delayMs);
  const skip = !settled || props.skip === true;

  const eltSyncQuery = useEltSyncLiteQuery({
    variables: { id: props.eltSyncId },
    skip,
  });

  const historyQuery = useEltSyncSnapshotStateQuery({
    variables: { syncId: props.eltSyncId },
    skip,
  });

  useEltSyncUpdatedListener(
    (event) => event.payload.syncId === props.eltSyncId,
    async () => {
      if (props.autoRefetch === false) {
        return;
      }
      devLog(
        "EltSyncSnapshotStateProvider - Received elt-sync:updated event for syncId:",
        props.eltSyncId,
      );
      await historyQuery.refetch();
      await eltSyncQuery.refetch();
    },
  );

  const sync = eltSyncQuery.data?.eltSync;
  const snapshotState = historyQuery.data?.eltSyncSnapshotState;

  const ctx = useMemo(() => {
    return {
      sync,
      snapshotState,
      loading: eltSyncQuery.loading || historyQuery.loading,
    };
  }, [sync, snapshotState, eltSyncQuery.loading, historyQuery.loading]);

  return ctx;
}

export const useEltSyncSnapshotStateContext = () => {
  const context = useContext(EltSnapshotStateContext);
  if (!context) {
    throw new Error(
      "useEltSyncSnapshotStateContext must be used within a EltSyncSnapshotStateProvider",
    );
  }
  return context;
};
